import React, { useState } from "react";

import {
  DefaultButton,
  ScrollablePane,
  Stack,
  Text,
  useFieldArray,
  useTheme
} from "@bps/fluent-ui";
import { Item, SelectionList } from "@components/SelectionList";
import { usePermissionsCheckQuery } from "@libs/api/gateways/env/env-gateway.hooks";
import { NO_ACTION_PERMISSION } from "@libs/permissions/permissions.constants";
import { Permissions } from "@libs/permissions/permissions.enum";

import { JobsConfigurationJob } from "./dtos";
import { JobDialogHost } from "./job";
import { JobListItem } from "./JobListItem";

interface JobListProps {
  selectedJob: JobsConfigurationJob | undefined;
  onSelectedItemChange: (item: Item) => void;
  onAddJob: (job: JobsConfigurationJob) => void;
  onUpdateJob: (job: JobsConfigurationJob) => void;
  onDeleteJob: (job: JobsConfigurationJob) => void;
}

export const JobList: React.FC<JobListProps> = ({
  selectedJob,
  onSelectedItemChange,
  onAddJob,
  onUpdateJob,
  onDeleteJob
}) => {
  const theme = useTheme();
  const [showAdd, setShowAdd] = useState<boolean>(false);

  const { data: hasWritePermission } = usePermissionsCheckQuery(
    Permissions.PltFieldManagementWrite
  );

  const { value: jobs } = useFieldArray<JobsConfigurationJob>("jobs");

  const jobItems =
    jobs
      ?.sort((a, b) =>
        !!a.code
          ? a.code.localeCompare(b.code)
          : a.name.localeCompare(b.name) ?? -1
      )
      .map(
        job =>
          ({
            id: job.id,
            name: job.name,
            node: (
              <JobListItem
                job={job}
                onDeleteJob={onDeleteJob}
                onUpdateJob={onUpdateJob}
              />
            ),
            requiredPermissions: [Permissions.PltFieldManagementRead]
          } as Item)
      ) ?? [];

  const selectedItem = jobItems.find(x => x.id === selectedJob?.id);

  return (
    <>
      <Stack verticalFill tokens={{ childrenGap: theme.spacing.s1 }}>
        <Stack horizontal horizontalAlign="space-between">
          <Text variant="xLarge">Jobs</Text>

          <Stack.Item align="end">
            <DefaultButton
              disabled={!hasWritePermission}
              title={!hasWritePermission ? NO_ACTION_PERMISSION : undefined}
              iconProps={{ iconName: "Add" }}
              onClick={() => setShowAdd(true)}
              styles={{ root: { width: "max-content", alignSelf: "center" } }}
            >
              Add Job
            </DefaultButton>
          </Stack.Item>
        </Stack>

        <Stack verticalFill styles={{ root: { position: "relative" } }}>
          <ScrollablePane>
            <SelectionList
              items={jobItems}
              selectedItem={selectedItem}
              onSelectedItemChange={onSelectedItemChange}
            />
          </ScrollablePane>
        </Stack>
      </Stack>

      <JobDialogHost
        modeLabel="Add"
        hidden={!showAdd}
        onDismiss={() => setShowAdd(false)}
        onSubmit={onAddJob}
      />
    </>
  );
};
